/**
 * Round 118 (Phase F1) — Business Operating System Dashboard.
 *
 * The customer's digital business headquarters. Shows completion %
 * across Brand / Business / Marketing / Sales / Operations, AI
 * recommendations, and every past versioned snapshot with a
 * download link. Big button "Create v{N+1} Snapshot" builds a
 * fresh ZIP containing every APPROVED asset at this moment in time.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Sparkle, Package, Download, CheckCircle, ArrowClockwise, ShareNetwork, Copy, X, Trash, ChatCircleText, CaretDown, CaretUp, EnvelopeSimple } from '@phosphor-icons/react';
import { businessOsAPI } from '@/utils/api';
import SnapshotDiffCard from '@/components/SnapshotDiffCard';

const CompletionCard = ({ label, percent, missing, testid }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5" data-testid={testid}>
    <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">{label}</div>
    <div className="text-3xl font-bold text-white mb-3">{percent}%</div>
    <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
      <div
        className="h-full rounded-full"
        style={{
          width: `${percent}%`,
          background: percent >= 80 ? '#22C55E' : percent >= 40 ? '#F59E0B' : '#EF4444',
        }}
      />
    </div>
    {missing && missing.length > 0 && (
      <div className="mt-3 text-[11px] text-slate-500">
        Missing: {missing.slice(0, 3).join(', ')}{missing.length > 3 ? '…' : ''}
      </div>
    )}
  </div>
);

const BusinessOSPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [shares, setShares] = useState([]);
  const [inbox, setInbox] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [shareModal, setShareModal] = useState(null); // holds snapshot
  const [lastLink, setLastLink] = useState(null);
  const [openedDiffs, setOpenedDiffs] = useState({}); // {version: diff}
  const [diffLoadingFor, setDiffLoadingFor] = useState(null);

  const refresh = async () => {
    try {
      const [d, s, sh, inb] = await Promise.all([
        businessOsAPI.dashboard(),
        businessOsAPI.snapshots(),
        businessOsAPI.listShares(),
        businessOsAPI.ownerInbox().catch(() => ({ comments: [] })),
      ]);
      setDashboard(d?.dashboard || null);
      setSnapshots(s?.snapshots || []);
      setShares(sh?.shares || []);
      setInbox(inb?.comments || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const toggleDiff = async (version) => {
    if (openedDiffs[version]) {
      setOpenedDiffs((prev) => { const next = { ...prev }; delete next[version]; return next; });
      return;
    }
    setDiffLoadingFor(version);
    try {
      const r = await businessOsAPI.snapshotDiff(version);
      setOpenedDiffs((prev) => ({ ...prev, [version]: r?.diff || null }));
    } catch (err) {
      toast.error('Could not load diff.');
    } finally {
      setDiffLoadingFor(null);
    }
  };

  const runCreateSnapshot = async () => {
    setCreating(true);
    try {
      const s = await businessOsAPI.createSnapshot();
      toast.success(`Snapshot v${s?.snapshot?.version} created.`);
      await refresh();
    } catch (err) {
      console.error(err);
      toast.error('Snapshot failed. Try again in a moment.');
    } finally {
      setCreating(false);
    }
  };

  const runDownloadSnapshot = async (version) => {
    try {
      const token = localStorage.getItem('token');
      const backend = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${backend}/api/business-os/snapshots/${version}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `business-os-v${version}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
    } catch (err) {
      console.error(err);
      toast.error('Download failed. Try again in a moment.');
    }
  };

  const buckets = dashboard?.buckets || {};
  const recs = dashboard?.recommendations || [];
  const overall = dashboard?.overall_completion ?? 0;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="business-os-page">
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur px-4 sm:px-8 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-400">
            <Package size={22} weight="duotone" />
          </div>
          <div>
            <div className="text-sm text-slate-400">Business OS</div>
            <div className="text-xl font-semibold text-white">Your digital business headquarters</div>
          </div>
        </div>
        <button
          onClick={runCreateSnapshot}
          disabled={creating || loading}
          data-testid="create-snapshot-btn"
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 font-semibold text-white hover:bg-emerald-400 disabled:opacity-40"
        >
          {creating ? (<><ArrowClockwise size={16} className="animate-spin" /> Building…</>)
                     : (<><Sparkle size={16} weight="fill" /> Create snapshot</>)}
        </button>
      </header>

      {loading && (
        <div className="p-10 text-slate-500">Loading your Business OS…</div>
      )}

      {!loading && dashboard && (
        <main className="p-6 sm:p-8 space-y-8">
          {/* Overall */}
          <section className="rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-900/40 p-8">
            <div className="text-sm uppercase tracking-widest text-slate-500 mb-2">Overall Business Completion</div>
            <div className="flex items-end gap-4 mb-4">
              <div className="text-6xl font-bold text-white" data-testid="overall-completion">{overall}%</div>
              <div className="text-slate-400 mb-2">Every approved asset counts toward this score.</div>
            </div>
            <div className="h-3 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-300" style={{ width: `${overall}%` }} />
            </div>
          </section>

          {/* Buckets */}
          <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {['brand', 'business', 'marketing', 'sales', 'operations'].map((k) => {
              const b = buckets[k] || {};
              return (
                <CompletionCard
                  key={k}
                  label={b.label || k}
                  percent={b.percent || 0}
                  missing={b.missing || []}
                  testid={`bucket-${k}`}
                />
              );
            })}
          </section>

          {/* Recommendations */}
          {recs.length > 0 && (
            <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6" data-testid="recommendations">
              <div className="flex items-center gap-2 mb-3">
                <Sparkle size={18} weight="fill" className="text-emerald-400" />
                <h2 className="text-lg font-semibold text-white">AI Recommendations</h2>
              </div>
              <ul className="space-y-2">
                {recs.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                    <CheckCircle size={16} weight="regular" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Snapshots */}
          <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6" data-testid="snapshots-section">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-white">Snapshots</h2>
                <p className="text-sm text-slate-500">
                  Each snapshot is a complete versioned ZIP of every approved asset at that moment.
                </p>
              </div>
              <div className="text-xs text-slate-500">
                {snapshots.length} version{snapshots.length === 1 ? '' : 's'}
              </div>
            </div>
            {snapshots.length === 0 && (
              <div className="rounded-lg border border-dashed border-slate-700 p-6 text-center">
                <p className="text-slate-400 text-sm mb-3">No snapshots yet.</p>
                <p className="text-slate-500 text-xs">Click &ldquo;Create snapshot&rdquo; above to package everything.</p>
              </div>
            )}
            <div className="space-y-2">
              {snapshots.map((s) => (
                <div key={s.id || s.version} data-testid={`snapshot-row-${s.version}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-slate-800 bg-slate-800/40 px-4 py-3">
                    <div className="min-w-0">
                      <div className="text-white font-semibold">Business OS v{s.version}</div>
                      <div className="text-xs text-slate-500 break-words">
                        {new Date(s.created_at).toLocaleString()} · {(s.size_bytes / 1024).toFixed(1)} KB · {Object.keys(s.asset_counts || {}).length} asset types
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => toggleDiff(s.version)}
                        data-testid={`snapshot-diff-toggle-${s.version}`}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 px-3 py-1.5 text-sm hover:bg-slate-700"
                      >
                        <Sparkle size={14} /> What changed
                        {openedDiffs[s.version] ? <CaretUp size={12} /> : <CaretDown size={12} />}
                      </button>
                      <button
                        onClick={() => { setShareModal(s); setLastLink(null); }}
                        data-testid={`snapshot-share-${s.version}`}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 px-3 py-1.5 text-sm hover:bg-slate-700"
                      >
                        <ShareNetwork size={14} /> Share
                      </button>
                      <button
                        type="button"
                        onClick={() => runDownloadSnapshot(s.version)}
                        data-testid={`snapshot-download-${s.version}`}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 px-3 py-1.5 text-sm hover:bg-emerald-500/25"
                      >
                        <Download size={14} /> Download
                      </button>
                    </div>
                  </div>
                  {(openedDiffs[s.version] !== undefined || diffLoadingFor === s.version) && (
                    <div className="mt-2 ml-2">
                      <SnapshotDiffCard
                        diff={openedDiffs[s.version]}
                        loading={diffLoadingFor === s.version}
                        theme="dark"
                        testid={`snapshot-diff-${s.version}`}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Active shares — audit view */}
          <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6" data-testid="shares-section">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-white">Active shares</h2>
              <p className="text-sm text-slate-500">
                Every recipient, expiration, and view/download event is tracked.
              </p>
            </div>
            {shares.length === 0 && (
              <p className="text-slate-500 text-sm">No active shares. Click &ldquo;Share&rdquo; on any snapshot to create one.</p>
            )}
            <div className="space-y-2">
              {shares.map((sh) => (
                <div
                  key={sh.id}
                  data-testid={`share-row-${sh.id}`}
                  className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border px-4 py-3 ${
                    sh.revoked ? 'border-red-900/60 bg-red-900/10' : 'border-slate-800 bg-slate-800/40'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-semibold flex items-center gap-2 flex-wrap break-words">
                      v{sh.snapshot_version} → <span className="break-all">{sh.recipient_email || sh.recipient_name || 'link only'}</span>
                      {sh.revoked && <span className="text-xs uppercase tracking-widest text-red-300">Revoked</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 break-words">
                      Perms: {(sh.permissions || []).join(', ')} · Sections: {(sh.sections || []).join(', ') || 'all'} ·
                      {' '}Views: {sh.metrics?.view ?? 0} · Downloads: {sh.metrics?.download ?? 0}
                    </div>
                    <div className="text-[11px] text-slate-600 mt-0.5">
                      Expires: {sh.expires_at ? new Date(sh.expires_at).toLocaleString() : 'never'}
                    </div>
                    {sh.email_status && (
                      <div
                        className={`text-[11px] mt-1 inline-flex items-center gap-1 rounded-md px-2 py-0.5 ${
                          sh.email_status.status === 'sent' ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/40'
                            : sh.email_status.status === 'failed' ? 'bg-red-500/10 text-red-300 border border-red-500/40'
                              : 'bg-amber-500/10 text-amber-300 border border-amber-500/40'
                        }`}
                        data-testid={`share-email-status-${sh.id}`}
                      >
                        <EnvelopeSimple size={11} />
                        Email: {sh.email_status.status}
                        {sh.email_status.status === 'skipped' && sh.email_status.reason
                          ? ` (${String(sh.email_status.reason).slice(0, 60)}…)` : ''}
                      </div>
                    )}
                  </div>
                  {!sh.revoked && (
                    <button
                      onClick={async () => {
                        try {
                          await businessOsAPI.revokeShare(sh.id);
                          toast.success('Share revoked.');
                          await refresh();
                        } catch (err) { toast.error('Revoke failed.'); }
                      }}
                      data-testid={`share-revoke-${sh.id}`}
                      className="inline-flex items-center gap-1 rounded-md border border-red-500/40 bg-red-500/10 text-red-300 px-3 py-1.5 text-xs hover:bg-red-500/20"
                    >
                      <Trash size={13} /> Revoke
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>

          {inbox.length > 0 && (
            <section className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6" data-testid="collab-inbox-section">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                    <ChatCircleText size={18} className="text-emerald-300" /> Feedback inbox
                  </h2>
                  <p className="text-sm text-slate-500">
                    Comments, approvals, and revision requests from every shared portal.
                  </p>
                </div>
                <div className="text-xs text-slate-500" data-testid="collab-inbox-count">
                  {inbox.length} entr{inbox.length === 1 ? 'y' : 'ies'}
                </div>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
                {inbox.slice(0, 25).map((c) => (
                  <div
                    key={c.id}
                    data-testid={`collab-inbox-row-${c.id}`}
                    className="rounded-lg border border-slate-800 bg-slate-800/40 px-3 py-2"
                  >
                    <div className="flex items-center flex-wrap gap-2 text-xs mb-1">
                      <span className="text-white font-semibold">{c.author_name || c.author_email || 'Guest'}</span>
                      <span className={`uppercase tracking-wider text-[10px] px-1.5 py-0.5 rounded-full ${
                        c.action === 'approve' ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40'
                          : c.action === 'request_revision' ? 'bg-amber-500/15 text-amber-300 border border-amber-500/40'
                            : 'bg-slate-700 text-slate-300 border border-slate-600'
                      }`}>{c.action}</span>
                      <span className="text-slate-500">v{c.snapshot_version} · {new Date(c.created_at).toLocaleString()}</span>
                      {c.status === 'resolved' && (
                        <span className="text-[10px] uppercase text-slate-500">resolved</span>
                      )}
                    </div>
                    {c.body && <div className="text-sm text-slate-200 whitespace-pre-wrap">{c.body}</div>}
                  </div>
                ))}
              </div>
            </section>
          )}

          {dashboard.generated_at && (
            <div className="text-xs text-slate-600 text-right">
              Last updated {new Date(dashboard.generated_at).toLocaleString()}
            </div>
          )}
        </main>
      )}

      {shareModal && (
        <ShareModal
          snapshot={shareModal}
          assetTypes={Object.keys(dashboard?.asset_counts || {})}
          onClose={() => { setShareModal(null); setLastLink(null); }}
          onCreated={async (link) => {
            setLastLink(link);
            await refresh();
          }}
          lastLink={lastLink}
        />
      )}
    </div>
  );
};


// ─── Share Modal ─────────────────────────────────────────────────
function ShareModal({ snapshot, assetTypes, onClose, onCreated, lastLink }) {
  const [sections, setSections] = useState([]);
  const [permissions, setPermissions] = useState(['view', 'download']);
  const [expiresBand, setExpiresBand] = useState('7d');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const togglePerm = (p) => setPermissions((prev) =>
    prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]);
  const toggleSection = (s) => setSections((prev) =>
    prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await businessOsAPI.createShare({
        snapshot_version: snapshot.version,
        sections, permissions,
        expires_band: expiresBand,
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        message,
      });
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      const portalUrl = `${window.location.origin}/portal/${r.token}`;
      onCreated({ share: r.share, portalUrl, token: r.token, backend });
      toast.success('Share link created — copy it before closing.');
    } catch (err) {
      console.error(err);
      toast.error('Failed to create share link.');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!lastLink?.portalUrl) return;
    try {
      await navigator.clipboard.writeText(lastLink.portalUrl);
      toast.success('Link copied.');
    } catch (_) { /* no-op */ }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="share-modal">
      <div className="w-full max-w-lg rounded-2xl border border-slate-700 bg-slate-900 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-white">Share Business OS v{snapshot.version}</h3>
            <p className="text-xs text-slate-500">Signed link. Revocable at any time.</p>
          </div>
          <button onClick={onClose} data-testid="share-modal-close" className="text-slate-500 hover:text-slate-200">
            <X size={18} />
          </button>
        </div>

        {!lastLink && (
          <>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Sections (empty = share everything)</label>
              <div className="flex flex-wrap gap-1.5">
                {assetTypes.map((t) => (
                  <button
                    key={t}
                    onClick={() => toggleSection(t)}
                    data-testid={`share-section-${t}`}
                    className={`rounded-full border px-2.5 py-0.5 text-[11px] ${
                      sections.includes(t)
                        ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-300'
                        : 'border-slate-700 bg-slate-800/60 text-slate-300 hover:bg-slate-800'
                    }`}
                  >{t}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Permissions</label>
              <div className="flex flex-wrap gap-1.5">
                {['view', 'download', 'comment', 'editor', 'admin'].map((p) => (
                  <button
                    key={p}
                    onClick={() => togglePerm(p)}
                    data-testid={`share-perm-${p}`}
                    disabled={p === 'editor' || p === 'admin'}
                    className={`rounded-full border px-2.5 py-0.5 text-[11px] ${
                      permissions.includes(p)
                        ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-300'
                        : 'border-slate-700 bg-slate-800/60 text-slate-400'
                    } disabled:opacity-40`}
                  >{p}{(p === 'editor' || p === 'admin') ? ' (soon)' : ''}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Expires</label>
              <select
                value={expiresBand}
                onChange={(e) => setExpiresBand(e.target.value)}
                data-testid="share-expires"
                className="w-full rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-sm text-slate-100"
              >
                <option value="24h">24 hours</option>
                <option value="7d">7 days</option>
                <option value="30d">30 days</option>
                <option value="never">Never</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <input type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="Recipient email (optional)" data-testid="share-email"
                className="rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-sm text-slate-100" />
              <input type="text" value={recipientName} onChange={(e) => setRecipientName(e.target.value)}
                placeholder="Name (optional)" data-testid="share-name"
                className="rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-sm text-slate-100" />
            </div>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Short message shown on the portal (optional)" rows={3} data-testid="share-message"
              className="w-full rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-sm text-slate-100 resize-y" />

            <button onClick={submit} disabled={busy} data-testid="share-create"
              className="w-full rounded-lg bg-emerald-500 py-2.5 font-semibold text-white hover:bg-emerald-400 disabled:opacity-40">
              {busy ? 'Creating…' : 'Generate share link'}
            </button>
          </>
        )}

        {lastLink && (
          <div className="space-y-3" data-testid="share-created-block">
            <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3">
              <div className="text-xs uppercase tracking-widest text-emerald-300 mb-1">Portal link (shown once)</div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={lastLink.portalUrl}
                  readOnly
                  data-testid="share-portal-url"
                  className="flex-1 rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-100"
                />
                <button onClick={copyLink} data-testid="share-copy"
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-500 text-white px-3 py-1.5 text-xs font-semibold">
                  <Copy size={12} /> Copy
                </button>
              </div>
            </div>
            <div className="text-xs text-slate-500">
              This link is signed. It won&rsquo;t be shown again. Anyone with it (and the granted permissions) can access the shared portal until it expires or you revoke it.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BusinessOSPage;
